import { resolveLink } from './resolveLink'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCheck } from 'lucide-react'
import { notificationsApi } from '@/api/notifications'
import { formatRelative } from '@/lib/dates'
import { stripHtml } from '@/lib/format'
import { Button, EmptyState, Spinner } from '@/components/ui'
import type { NotificationItem } from '@/types/user'
import styles from './NotificationsMenu.module.scss'

interface Props {
  trigger: (toggle: () => void, unseen: number) => ReactNode
}

export function NotificationsMenu({ trigger }: Props) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const countQuery = useQuery({ queryKey: ['notifications', 'count'], queryFn: notificationsApi.count, refetchInterval: 30 * 1000 })
  const listQuery = useQuery({
    queryKey: ['notifications', 'recent'],
    queryFn: () => notificationsApi.list({ page: 1, limit: 8 }),
    enabled: open,
  })
  const markRead = useMutation({
    mutationFn: (ids: number[]) => notificationsApi.markRead(ids),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })

  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const items = listQuery.data?.notifications ?? []
  const unseenIds = items.filter((n) => !n.seen).map((n) => n.id)

  function openItem(n: NotificationItem) {
    if (!n.seen) markRead.mutate([n.id])
    setOpen(false)
    const target = resolveLink(n.action_link)
    if (target) navigate(target)
  }

  return (
    <div ref={ref} className={styles.wrap}>
      {trigger(() => setOpen((o) => !o), countQuery.data ?? 0)}
      {open && (
        <div className={styles.panel} role="dialog" aria-label="Notifications">
          <div className={styles.head}>
            <strong>Notifications</strong>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<CheckCheck size={14} />}
              disabled={!unseenIds.length || markRead.isPending}
              onClick={() => markRead.mutate(unseenIds)}
            >
              Mark all read
            </Button>
          </div>
          <div className={styles.list}>
            {listQuery.isPending ? (
              <Spinner />
            ) : items.length === 0 ? (
              <EmptyState compact title="You're all caught up" />
            ) : (
              items.map((n) => (
                <button key={n.id} type="button" className={styles.item} data-unseen={!n.seen || undefined} onClick={() => openItem(n)}>
                  <span className={styles.dot} aria-hidden="true" />
                  <span className={styles.body}>
                    <span className={styles.text}>{stripHtml(n.data)}</span>
                    <span className={styles.meta}>
                      {[n.firstname, n.lastname].filter(Boolean).join(' ') || 'System'} · {formatRelative(n.created_at)}
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>
          <Link to="/notifications" className={styles.footer} onClick={() => setOpen(false)}>
            View all notifications
          </Link>
        </div>
      )}
    </div>
  )
}
